// src/hooks/useMinhaAlcada.ts
// Parâmetros de aprovação DO PRÓPRIO usuário logado, para o bloco de alçada da
// Home. Reusa o mesmo contrato que `ApproverParamsSection` já consome no painel
// de usuários — nenhuma rota nova.
//
// FAIL-CLOSED, ao contrário do resto do projeto: qualquer erro esconde o bloco.
// É o oposto de `useConfiguracaoLogin` (que é fail-open) e a diferença é
// deliberada — mostrar um limite de aprovação errado é pior que não mostrar
// limite nenhum, enquanto esconder um método de login por falha de rede
// impediria o usuário de entrar.
//
// LIMITAÇÃO CONHECIDA E ACEITA: `GET /v1/permissoes/{id}/parametros-aprovacao` é
// gateado pelo backend por `usuarios.editar_parametros_aprovacao` — permissão de
// quem ADMINISTRA aprovadores, não de quem é aprovador. O aprovador ROLE_USER
// não consegue ler a própria alçada, então o bloco não aparece para ele (e o
// request nem sai — ver gate abaixo). Não há rota "self" hoje. Quando o backend
// expuser algo como `GET /v1/permissoes/me/parametros-aprovacao`, trocar a URL e
// afrouxar o gate de permissão basta — nada mais nesta tela muda.
import { useEffect, useState } from 'react';
import { apiGet } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import type { UsuarioParametrosAprovacao } from '@/types/aprovacao/Aprovacao';
import { logger } from '@/utils/logger';

export interface MinhaAlcada {
  /** `null` enquanto carrega, em erro/403, ou quando o bloco não se aplica. */
  dados: UsuarioParametrosAprovacao | null;
  isLoading: boolean;
}

export function useMinhaAlcada(): MinhaAlcada {
  const { isInitialized, userId, role, hasPermission } = useAuth();
  const [dados, setDados] = useState<UsuarioParametrosAprovacao | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Duas condições, por motivos diferentes:
  //
  // 1. ROLE_MASTER não tem limite de aprovação, categoria permitida nem produto
  //    excluído — o bloco não se aplica a ele. Regra de PRODUTO, não de
  //    permissão (MASTER passaria em qualquer gate).
  // 2. Sem `usuarios.editar_parametros_aprovacao` o backend responde 403. Sem
  //    este gate, todo ROLE_USER geraria um 403 inútil em CADA carregamento da
  //    Home — requisição desperdiçada e ruído permanente no log do backend.
  //    Mesmo gate que `Usuarios.tsx` aplica na sub-seção de parâmetros.
  //
  // `aplicavel` é booleano (primitivo) de propósito: `hasPermission` não é
  // memoizado no AuthContext, e usar sua identidade como dep de efeito
  // refaria a chamada a cada re-render do AuthProvider.
  const aplicavel =
    role !== 'ROLE_MASTER' && userId !== null && hasPermission('usuarios.editar_parametros_aprovacao');

  useEffect(() => {
    if (!isInitialized) return;
    if (!aplicavel) {
      setDados(null);
      setIsLoading(false);
      return;
    }

    let cancelado = false;

    const carregar = async () => {
      setIsLoading(true);
      try {
        const res = await apiGet(`/v1/permissoes/${userId}/parametros-aprovacao`, { silent: true });
        if (cancelado) return;
        setDados(res.data as UsuarioParametrosAprovacao);
      } catch (err) {
        if (cancelado) return;
        // 403 (sem a permissão de admin) e 400 ("não encontrado", usuário sem
        // parâmetros) são ESPERADOS aqui — não são falha, são ausência de dado.
        // Nem alerta nem toast: o usuário não fez nada errado.
        // Só a mensagem no log: o erro Axios cru carrega o Bearer token (§E).
        logger.log(
          'Alçada não disponível para este usuário (bloco oculto):',
          err instanceof Error ? err.message : String(err)
        );
        setDados(null);
      } finally {
        if (!cancelado) setIsLoading(false);
      }
    };

    carregar();

    return () => {
      cancelado = true;
    };
  }, [isInitialized, aplicavel, userId]);

  return { dados, isLoading };
}
